// src/components/shared/AccessibilityWidget.tsx
// רכיב נגישות תואם תקן ישראלי AA

import React, { useState, useEffect } from "react";

interface AccessibilityOptions {
  highContrast: boolean;
  largeText: boolean;
  underlineLinks: boolean;
  focusHighlight: boolean;
  stopAnimations: boolean;
}

const AccessibilityWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<AccessibilityOptions>({
    highContrast: false,
    largeText: false,
    underlineLinks: false,
    focusHighlight: false,
    stopAnimations: false,
  });

  // Load saved accessibility preferences
  useEffect(() => {
    const saved = localStorage.getItem("accessibility-preferences");
    if (saved) {
      try {
        const parsedOptions = JSON.parse(saved);
        setOptions(parsedOptions);
        applyAccessibilityOptions(parsedOptions);
      } catch (e) {
        console.warn("Could not parse accessibility preferences");
      }
    }

    // Add CSS styles to head
    addAccessibilityStyles();
  }, []);

  // Add CSS styles dynamically
  const addAccessibilityStyles = () => {
    if (document.getElementById("accessibility-styles")) return;

    const style = document.createElement("style");
    style.id = "accessibility-styles";
    style.innerHTML = `
      /* High Contrast */
      .accessibility-high-contrast {
        filter: contrast(150%) brightness(1.1) !important;
      }
      
      .accessibility-high-contrast * {
        text-shadow: none !important;
        box-shadow: none !important;
      }

      /* Large Text */
      .accessibility-large-text {
        font-size: 120% !important;
      }

      .accessibility-large-text * {
        line-height: 1.6 !important;
      }

      /* Underline Links */
      .accessibility-underline-links a {
        text-decoration: underline !important;
      }

      /* Focus Highlight */
      .accessibility-focus-highlight *:focus {
        outline: 3px solid #3b82f6 !important;
        outline-offset: 2px !important;
      }

      /* No Animations */
      .accessibility-no-animations *,
      .accessibility-no-animations *:before,
      .accessibility-no-animations *:after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
        scroll-behavior: auto !important;
      }
    `;
    document.head.appendChild(style);
  };

  // Apply accessibility options to document
  const applyAccessibilityOptions = (newOptions: AccessibilityOptions) => {
    const body = document.body;
    const html = document.documentElement;

    // High contrast
    if (newOptions.highContrast) {
      body.classList.add("accessibility-high-contrast");
    } else {
      body.classList.remove("accessibility-high-contrast");
    }

    // Large text
    if (newOptions.largeText) {
      html.classList.add("accessibility-large-text");
    } else {
      html.classList.remove("accessibility-large-text");
    }

    // Underline links
    if (newOptions.underlineLinks) {
      body.classList.add("accessibility-underline-links");
    } else {
      body.classList.remove("accessibility-underline-links");
    }

    // Focus highlight
    if (newOptions.focusHighlight) {
      body.classList.add("accessibility-focus-highlight");
    } else {
      body.classList.remove("accessibility-focus-highlight");
    }

    // Stop animations
    if (newOptions.stopAnimations) {
      body.classList.add("accessibility-no-animations");
    } else {
      body.classList.remove("accessibility-no-animations");
    }
  };

  const updateOption = (key: keyof AccessibilityOptions, value: boolean) => {
    const newOptions = { ...options, [key]: value };
    setOptions(newOptions);
    applyAccessibilityOptions(newOptions);
    localStorage.setItem(
      "accessibility-preferences",
      JSON.stringify(newOptions)
    );
  };

  const resetAll = () => {
    const resetOptions = {
      highContrast: false,
      largeText: false,
      underlineLinks: false,
      focusHighlight: false,
      stopAnimations: false,
    };
    setOptions(resetOptions);
    applyAccessibilityOptions(resetOptions);
    localStorage.removeItem("accessibility-preferences");
  };

  return (
    <div className="fixed left-4 top-4 z-50">
      {/* Accessibility Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
    w-14 h-14 rounded-full shadow-lg transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-500
          ${
            isOpen || Object.values(options).some(Boolean)
              ? "bg-blue-600 text-white"
              : "bg-white text-gray-700 hover:bg-gray-50"
          }
        `}
        aria-label="פתח תפריט נגישות"
        aria-expanded={isOpen}
        title="נגישות"
      >
        <svg
          className="w-6 h-6 mx-auto"
          fill="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle cx="12" cy="4" r="2" />
          <path d="M19 13v-2c-1.54.02-3.09-.75-4.07-1.83l-1.29-1.43c-.17-.19-.38-.34-.61-.45-.01 0-.01-.01-.02-.01H13c-.35-.2-.75-.3-1.19-.26C10.76 7.11 10 8.04 10 9.09V15c0 1.1.9 2 2 2h5v5h2v-5.5c0-1.1-.9-2-2-2h-3v-3.45c1.29 1.07 3.25 1.94 5 1.95zm-6.17 5c-.41 1.16-1.52 2-2.83 2-1.66 0-3-1.34-3-3 0-1.31.84-2.41 2-2.83V12.1c-2.28.46-4 2.48-4 4.9 0 2.76 2.24 5 5 5 2.42 0 4.44-1.72 4.9-4h-2.07z" />
        </svg>
      </button>

      {/* Accessibility Panel */}
      {isOpen && (
        <div
          className="absolute top-14 left-0 w-72 bg-white rounded-lg shadow-xl border p-4 z-50"
          dir="rtl"
          role="dialog"
          aria-labelledby="accessibility-title"
        >
          <div className="flex items-center justify-between mb-4">
            <h3
              id="accessibility-title"
              className="text-lg font-semibold text-gray-900"
            >
              הגדרות נגישות
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="סגור תפריט נגישות"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            {/* High Contrast */}
            <div>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-gray-700">ניגודיות גבוהה</span>
                <input
                  type="checkbox"
                  checked={options.highContrast}
                  onChange={(e) =>
                    updateOption("highContrast", e.target.checked)
                  }
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  aria-describedby="contrast-desc"
                />
              </label>
              <p id="contrast-desc" className="text-xs text-gray-500 mt-1">
                הגברת הניגודיות לקריאה טובה יותר
              </p>
            </div>

            {/* Large Text */}
            <div>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-gray-700">טקסט גדול</span>
                <input
                  type="checkbox"
                  checked={options.largeText}
                  onChange={(e) => updateOption("largeText", e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  aria-describedby="text-desc"
                />
              </label>
              <p id="text-desc" className="text-xs text-gray-500 mt-1">
                הגדלת גודל הטקסט ב-200%
              </p>
            </div>

            {/* Underline Links */}
            <div>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-gray-700">קו תחתון לקישורים</span>
                <input
                  type="checkbox"
                  checked={options.underlineLinks}
                  onChange={(e) =>
                    updateOption("underlineLinks", e.target.checked)
                  }
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  aria-describedby="links-desc"
                />
              </label>
              <p id="links-desc" className="text-xs text-gray-500 mt-1">
                הוספת קו תחתון לכל הקישורים
              </p>
            </div>

            {/* Focus Highlight */}
            <div>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-gray-700">הדגשת פוקוס</span>
                <input
                  type="checkbox"
                  checked={options.focusHighlight}
                  onChange={(e) =>
                    updateOption("focusHighlight", e.target.checked)
                  }
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  aria-describedby="focus-desc"
                />
              </label>
              <p id="focus-desc" className="text-xs text-gray-500 mt-1">
                הדגשה ברורה יותר של רכיבים בפוקוס
              </p>
            </div>

            {/* Stop Animations */}
            <div>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-gray-700">הפסקת אנימציות</span>
                <input
                  type="checkbox"
                  checked={options.stopAnimations}
                  onChange={(e) =>
                    updateOption("stopAnimations", e.target.checked)
                  }
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  aria-describedby="animations-desc"
                />
              </label>
              <p id="animations-desc" className="text-xs text-gray-500 mt-1">
                הפסקת כל האנימציות והמעברים
              </p>
            </div>
          </div>

          {/* Reset Button */}
          <div className="mt-6 pt-4 border-t">
            <button
              onClick={resetAll}
              className="w-full py-2 px-4 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              איפוס הגדרות נגישות
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessibilityWidget;
